import { supabase } from "@/shared/lib/supabaseClient";

/**
 * Camada de acesso a dados do Núcleo — verificação de autorização de
 * Nível 2 (DA-002): Função Ministerial com permissão de gestão em um módulo.
 *
 * A lógica em si (qual ministério é responsável pelo módulo, se a função da
 * pessoa está ativa, se essa função tem pode_gerenciar_modulo) mora inteira
 * no banco, na função `gerencia_modulo()` criada na migration 0016 — este
 * service só expõe uma chamada tipada e conveniente para o frontend, sem
 * duplicar a regra de negócio no cliente.
 */
export const acessoService = {
  async podeGerenciarModulo(moduloCodigo: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("gerencia_modulo", {
      p_modulo_codigo: moduloCodigo,
    });

    if (error) {
      // Falha ao verificar é tratada como "sem permissão" (mais seguro que
      // liberar acesso em caso de erro de rede/RPC).
      return false;
    }

    return Boolean(data);
  },

  async podeGerenciarBiblioteca(): Promise<boolean> {
    return acessoService.podeGerenciarModulo("biblioteca");
  },

  /** Nível 2 (DA-002): gestão do módulo Eventos (Sprint 3). */
  async podeGerenciarEventos(): Promise<boolean> {
    return acessoService.podeGerenciarModulo("eventos");
  },

  /** Nível 2 (DA-002): gestão do módulo Reservas/Ambientes (Sprint 3). */
  async podeGerenciarReservas(): Promise<boolean> {
    return acessoService.podeGerenciarModulo("reservas");
  },

  /**
   * Admin, OU gestor de módulo, OU líder de QUALQUER ministério vinculado ao
   * módulo (migration 0029) — quem pode aprovar/rejeitar reservas pendentes
   * e gerenciar reservas de outras pessoas. Mais amplo que
   * `podeGerenciarModulo`, que só considera funções com pode_gerenciar_modulo
   * (deixava de fora o líder que não tem esse marcador extra).
   */
  async podeGerenciarOuLiderarModulo(moduloCodigo: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("gerencia_ou_lidera_modulo", {
      p_modulo_codigo: moduloCodigo,
    });
    if (error) return false;
    return Boolean(data);
  },

  /** Aprova/rejeita solicitações de Reservas e gerencia reservas de terceiros. */
  async podeAprovarReservas(): Promise<boolean> {
    return acessoService.podeGerenciarOuLiderarModulo("reservas");
  },

  /** Nível 1 (DA-002): Perfil Administrador da Plataforma. */
  async ehAdminPlataforma(): Promise<boolean> {
    const { data, error } = await supabase.rpc("eh_admin_plataforma");
    if (error) return false;
    return Boolean(data);
  },

  /**
   * G6 (DA-002 — visibilidade é separada de permissão): códigos dos módulos
   * que a pessoa logada pode VER no menu. A regra mora inteira no banco, na
   * função `modulos_visiveis()` (migration 0023): módulo ativo + visibilidade
   * 'todos_membros' ou vínculo ativo no ministério responsável; admin da
   * plataforma e quem gerencia o módulo sempre veem.
   */
  async listarModulosVisiveis(): Promise<string[]> {
    const { data, error } = await supabase.rpc("modulos_visiveis");
    // Falha ao consultar = menu vazio (mais seguro que mostrar tudo).
    if (error) return [];
    return (data ?? []) as string[];
  },

  /**
   * F6-14 (revisão 21/07/2026): 'compartilhado' (padrão) ou 'individual' —
   * só faz diferença quando o módulo tem 2+ ministérios responsáveis
   * (migration 0030/0048). Lido direto de `igreja_modulos` (SELECT liberado
   * a qualquer autenticado).
   */
  async modoDadosModulo(moduloCodigo: string): Promise<"compartilhado" | "individual"> {
    const { data, error } = await supabase
      .from("igreja_modulos")
      .select("modo_dados")
      .eq("modulo_codigo", moduloCodigo)
      .maybeSingle();
    if (error || !data) return "compartilhado";
    return (data.modo_dados as "compartilhado" | "individual" | null) ?? "compartilhado";
  },

  /**
   * F6-14: ministérios pelos quais a pessoa atual gerencia especificamente
   * este módulo (mesmo critério de `gerencia_modulo`, mas devolvendo os IDs
   * em vez de um booleano) — usado para restringir escolhas e botões quando
   * o módulo está em modo "individual" (migration 0048).
   */
  async minhasMinisteriosGestaoModulo(moduloCodigo: string): Promise<string[]> {
    const { data, error } = await supabase.rpc("minhas_ministerios_gestao_modulo", {
      p_modulo_codigo: moduloCodigo,
    });
    if (error) return [];
    return (data ?? []) as string[];
  },
};
